<div class="cp-dialog">
  <!-- Header -->
  <div class="cp-header">
    <div class="cp-header__icon">
      <mat-icon>lock_reset</mat-icon>
    </div>
    <div class="cp-header__titles">
      <h2 mat-dialog-title class="cp-title" [translate]="'changePasswordDialog.title'">
        Change password
      </h2>
      <p class="cp-subtitle" [translate]="'changePasswordDialog.subtitle'">
        Keep your account secure with a new password.
      </p>
    </div>
    <button
      mat-icon-button
      type="button"
      class="cp-close"
      (click)="cancel()"
      [attr.aria-label]="'entity.action.cancel' | translate"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div mat-dialog-content class="cp-content">
    <!-- Success state -->
    <div *ngIf="success" class="cp-success">
      <mat-icon class="cp-success__icon">check_circle</mat-icon>
      <p class="cp-success__text" [translate]="'changePasswordDialog.messages.success'">
        Your password has been changed successfully.
      </p>
    </div>

    <form
      *ngIf="!success"
      class="cp-form"
      [formGroup]="form"
      (ngSubmit)="submit()"
      autocomplete="off"
      novalidate
    >
      <!-- Current password -->
      <mat-form-field appearance="outline" class="cp-field">
        <mat-label [translate]="'changePasswordDialog.fields.currentPassword'">
          Current password
        </mat-label>
        <input
          matInput
          [type]="hideCurrent ? 'password' : 'text'"
          formControlName="currentPassword"
          autocomplete="current-password"
        />
        <button
          type="button"
          mat-icon-button
          matSuffix
          tabindex="-1"
          class="cp-eye"
          (click)="hideCurrent = !hideCurrent"
          [attr.aria-label]="'changePasswordDialog.toggleVisibility' | translate"
        >
          <mat-icon>{{ hideCurrent ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <mat-error
          *ngIf="hasError('currentPassword', 'required')"
          [translate]="'changePasswordDialog.errors.currentRequired'"
        >
          Current password is required.
        </mat-error>
      </mat-form-field>

      <!-- New password -->
      <mat-form-field appearance="outline" class="cp-field">
        <mat-label [translate]="'changePasswordDialog.fields.newPassword'">New password</mat-label>
        <input
          matInput
          [type]="hideNew ? 'password' : 'text'"
          formControlName="newPassword"
          autocomplete="new-password"
        />
        <button
          type="button"
          mat-icon-button
          matSuffix
          tabindex="-1"
          class="cp-eye"
          (click)="hideNew = !hideNew"
          [attr.aria-label]="'changePasswordDialog.toggleVisibility' | translate"
        >
          <mat-icon>{{ hideNew ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <mat-error
          *ngIf="hasError('newPassword', 'required')"
          [translate]="'changePasswordDialog.errors.newRequired'"
        >
          New password is required.
        </mat-error>
        <mat-error
          *ngIf="hasError('newPassword', 'minlength')"
          [translate]="'changePasswordDialog.errors.minlength'"
          [translateParams]="{ min: minLength }"
        >
          Password is too short.
        </mat-error>
        <mat-error
          *ngIf="hasError('newPassword', 'maxlength')"
          [translate]="'changePasswordDialog.errors.maxlength'"
          [translateParams]="{ max: maxLength }"
        >
          Password is too long.
        </mat-error>
      </mat-form-field>

      <!-- Strength meter -->
      <div class="cp-strength" *ngIf="form.get('newPassword')?.value" [attr.data-level]="strengthLevel">
        <div class="cp-strength__bars">
          <span
            class="cp-strength__bar"
            *ngFor="let i of [1, 2, 3, 4, 5]"
            [class.active]="i <= strengthLevel"
          ></span>
        </div>
        <small class="cp-strength__label">
          <span [translate]="'changePasswordDialog.strength.label'">Password strength:</span>
          <span class="cp-strength__value">
            {{ 'changePasswordDialog.strength.level' + strengthLevel | translate }}
          </span>
        </small>
      </div>

      <!-- Confirm password -->
      <mat-form-field appearance="outline" class="cp-field">
        <mat-label [translate]="'changePasswordDialog.fields.confirmPassword'">
          Confirm new password
        </mat-label>
        <input
          matInput
          [type]="hideConfirm ? 'password' : 'text'"
          formControlName="confirmPassword"
          autocomplete="new-password"
        />
        <button
          type="button"
          mat-icon-button
          matSuffix
          tabindex="-1"
          class="cp-eye"
          (click)="hideConfirm = !hideConfirm"
          [attr.aria-label]="'changePasswordDialog.toggleVisibility' | translate"
        >
          <mat-icon>{{ hideConfirm ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
        <mat-error
          *ngIf="hasError('confirmPassword', 'required')"
          [translate]="'changePasswordDialog.errors.confirmRequired'"
        >
          Confirmation is required.
        </mat-error>
      </mat-form-field>

      <!-- Cross-field errors -->
      <div
        class="cp-form-error"
        *ngIf="form.hasError('doNotMatch') && form.get('confirmPassword')?.touched"
      >
        <mat-icon>error_outline</mat-icon>
        <span [translate]="'changePasswordDialog.errors.doNotMatch'">
          The passwords do not match.
        </span>
      </div>
      <div
        class="cp-form-error"
        *ngIf="form.hasError('sameAsCurrent') && form.get('newPassword')?.touched"
      >
        <mat-icon>error_outline</mat-icon>
        <span [translate]="'changePasswordDialog.errors.sameAsCurrent'">
          The new password must be different from the current one.
        </span>
      </div>
    </form>
  </div>

  <!-- Footer -->
  <div mat-dialog-actions class="cp-actions" *ngIf="!success">
    <button
      type="button"
      mat-button
      class="cp-btn cp-btn--ghost"
      [disabled]="isSaving"
      (click)="cancel()"
      [translate]="'entity.action.cancel'"
    >
      Cancel
    </button>
    <button
      type="button"
      mat-flat-button
      color="primary"
      class="cp-btn cp-btn--primary"
      [disabled]="form.invalid || isSaving"
      (click)="submit()"
    >
      <mat-spinner *ngIf="isSaving" diameter="18"></mat-spinner>
      <span [translate]="'changePasswordDialog.actions.save'">Change password</span>
    </button>
  </div>
</div>
